// =============================================================================
// ENTITY CONFIG PANEL - Right sidebar for editing selected entity
// =============================================================================

import { useState } from 'react';
import {
  X,
  Plus,
  Trash2,
  GripVertical,
  ChevronDown,
  ChevronRight,
  Database,
  Key,
  Hash,
} from 'lucide-react';
import { cn } from '../../../lib/utils';
import { useDataModelerStore } from '../store';
import type { EntityField, FieldType, RelationType } from '../types';

const FIELD_TYPES: { value: FieldType; label: string }[] = [
  { value: 'string', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'date', label: 'Date' },
  { value: 'datetime', label: 'Date & Time' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'url', label: 'URL' },
  { value: 'json', label: 'JSON' },
  { value: 'file', label: 'File' },
  { value: 'enum', label: 'Enum' },
  { value: 'relation', label: 'Relation' },
];

// Relation types for future use in relation field configuration
const _RELATION_TYPES: { value: RelationType; label: string }[] = [
  { value: 'one-to-one', label: '1:1 (One to One)' },
  { value: 'one-to-many', label: '1:N (One to Many)' },
  { value: 'many-to-one', label: 'N:1 (Many to One)' },
  { value: 'many-to-many', label: 'N:N (Many to Many)' },
];
void _RELATION_TYPES;

interface FieldRowProps {
  field: EntityField;
  entityId: string;
  onUpdate: (fieldId: string, updates: Partial<EntityField>) => void;
  onRemove: (fieldId: string) => void;
}

function FieldRow({ field, entityId, onUpdate, onRemove }: FieldRowProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const entities = useDataModelerStore((s) => s.entities);
  const otherEntities = entities.filter((e) => e.id !== entityId);

  return (
    <div
      className={cn(
        'border border-secondary-200 dark:border-secondary-700 rounded-lg',
        field.isPrimitive && 'bg-primary-50/50 dark:bg-primary-900/10'
      )}
    >
      {/* Field header */}
      <div className="flex items-center gap-2 p-2">
        <button className="cursor-grab text-secondary-400 hover:text-secondary-600">
          <GripVertical className="h-4 w-4" />
        </button>

        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-secondary-400 hover:text-secondary-600"
        >
          {isExpanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </button>

        <input
          type="text"
          value={field.name}
          onChange={(e) => onUpdate(field.id, { name: e.target.value })}
          disabled={field.isPrimitive}
          className={cn(
            'flex-1 text-sm font-medium bg-transparent border-0 focus:ring-0 p-0',
            'text-secondary-900 dark:text-white',
            field.isPrimitive && 'text-primary-700 dark:text-primary-400'
          )}
        />

        {field.required && <span title="Required"><Key className="h-3 w-3 text-amber-500" /></span>}
        {field.unique && <span title="Unique"><Hash className="h-3 w-3 text-purple-500" /></span>}
        {field.indexed && <span title="Indexed"><Database className="h-3 w-3 text-blue-500" /></span>}

        {!field.isPrimitive && (
          <button
            onClick={() => onRemove(field.id)}
            className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-secondary-400 hover:text-red-600"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        )}
      </div>

      {/* Field details */}
      {isExpanded && (
        <div className="p-3 border-t border-secondary-200 dark:border-secondary-700 space-y-3">
          {/* Type */}
          <div>
            <label className="text-xs text-secondary-500 block mb-1">Type</label>
            <select
              value={field.type}
              onChange={(e) => onUpdate(field.id, { type: e.target.value as FieldType })}
              disabled={field.isPrimitive}
              className="w-full text-sm rounded border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-800"
            >
              {FIELD_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Enum values */}
          {field.type === 'enum' && (
            <div>
              <label className="text-xs text-secondary-500 block mb-1">
                Values (comma separated)
              </label>
              <input
                type="text"
                value={field.enumValues?.join(', ') || ''}
                onChange={(e) =>
                  onUpdate(field.id, {
                    enumValues: e.target.value.split(',').map((v) => v.trim()).filter(Boolean),
                  })
                }
                placeholder="value1, value2, value3"
                className="w-full text-sm rounded border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-800"
              />
            </div>
          )}

          {/* Relation target */}
          {field.type === 'relation' && (
            <div>
              <label className="text-xs text-secondary-500 block mb-1">Related Entity</label>
              <select
                value={field.relationTarget || ''}
                onChange={(e) => onUpdate(field.id, { relationTarget: e.target.value })}
                className="w-full text-sm rounded border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-800"
              >
                <option value="">Select entity...</option>
                {otherEntities.map((entity) => (
                  <option key={entity.id} value={entity.id}>
                    {entity.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Flags */}
          <div className="flex flex-wrap gap-3">
            <label className="flex items-center gap-1.5 text-xs">
              <input
                type="checkbox"
                checked={field.required}
                onChange={(e) => onUpdate(field.id, { required: e.target.checked })}
                className="rounded border-secondary-300"
              />
              Required
            </label>
            <label className="flex items-center gap-1.5 text-xs">
              <input
                type="checkbox"
                checked={field.unique || false}
                onChange={(e) => onUpdate(field.id, { unique: e.target.checked })}
                className="rounded border-secondary-300"
              />
              Unique
            </label>
            <label className="flex items-center gap-1.5 text-xs">
              <input
                type="checkbox"
                checked={field.indexed || false}
                onChange={(e) => onUpdate(field.id, { indexed: e.target.checked })}
                className="rounded border-secondary-300"
              />
              Indexed
            </label>
          </div>

          {/* Description */}
          <div>
            <label className="text-xs text-secondary-500 block mb-1">Description</label>
            <input
              type="text"
              value={field.description || ''}
              onChange={(e) => onUpdate(field.id, { description: e.target.value })}
              placeholder="Field description..."
              className="w-full text-sm rounded border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-800"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export function EntityConfigPanel() {
  const selectedEntityId = useDataModelerStore((s) => s.selectedEntityId);
  const entities = useDataModelerStore((s) => s.entities);
  const updateEntity = useDataModelerStore((s) => s.updateEntity);
  const updateField = useDataModelerStore((s) => s.updateField);
  const addField = useDataModelerStore((s) => s.addField);
  const removeField = useDataModelerStore((s) => s.removeField);
  const selectEntity = useDataModelerStore((s) => s.selectEntity);

  const selectedEntity = entities.find((e) => e.id === selectedEntityId);

  if (!selectedEntity) {
    return (
      <div className="flex items-center justify-center h-full p-4 text-secondary-500">
        Select an entity to edit
      </div>
    );
  }

  const primitiveFields = selectedEntity.fields.filter((f) => f.isPrimitive);
  const customFields = selectedEntity.fields.filter((f) => !f.isPrimitive);

  const handleAddField = () => {
    addField(selectedEntity.id, {
      name: 'newField',
      type: 'string',
      required: false,
    });
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-secondary-200 dark:border-secondary-700 flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-secondary-900 dark:text-white">
            Entity Configuration
          </h3>
          <p className="text-xs text-secondary-500 mt-0.5">
            {selectedEntity.primitive} primitive
          </p>
        </div>
        <button
          onClick={() => selectEntity(null)}
          className="p-1 rounded hover:bg-secondary-100 dark:hover:bg-secondary-800"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Entity name */}
        <div>
          <label className="text-sm font-medium text-secondary-700 dark:text-secondary-300 block mb-1">
            Entity Name
          </label>
          <input
            type="text"
            value={selectedEntity.name}
            onChange={(e) => updateEntity(selectedEntity.id, { name: e.target.value })}
            className="w-full rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
          />
        </div>

        {/* Entity color */}
        <div>
          <label className="text-sm font-medium text-secondary-700 dark:text-secondary-300 block mb-1">
            Color
          </label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={selectedEntity.color}
              onChange={(e) => updateEntity(selectedEntity.id, { color: e.target.value })}
              className="w-10 h-10 rounded cursor-pointer"
            />
            <input
              type="text"
              value={selectedEntity.color}
              onChange={(e) => updateEntity(selectedEntity.id, { color: e.target.value })}
              className="flex-1 rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-800 text-sm"
            />
          </div>
        </div>

        {/* Primitive fields section */}
        {primitiveFields.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2 flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-primary-500" />
              Primitive Fields
            </h4>
            <div className="space-y-2">
              {primitiveFields.map((field) => (
                <FieldRow
                  key={field.id}
                  field={field}
                  entityId={selectedEntity.id}
                  onUpdate={(fieldId, updates) => updateField(selectedEntity.id, fieldId, updates)}
                  onRemove={(fieldId) => removeField(selectedEntity.id, fieldId)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Custom fields section */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-secondary-700 dark:text-secondary-300 flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-secondary-400" />
              Custom Fields
            </h4>
            <button
              onClick={handleAddField}
              className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400"
            >
              <Plus className="h-3 w-3" />
              Add Field
            </button>
          </div>
          {customFields.length > 0 ? (
            <div className="space-y-2">
              {customFields.map((field) => (
                <FieldRow
                  key={field.id}
                  field={field}
                  entityId={selectedEntity.id}
                  onUpdate={(fieldId, updates) => updateField(selectedEntity.id, fieldId, updates)}
                  onRemove={(fieldId) => removeField(selectedEntity.id, fieldId)}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-4 text-sm text-secondary-500 border border-dashed border-secondary-300 dark:border-secondary-600 rounded-lg">
              No custom fields yet
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
